import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { type KeyboardEvent, useEffect, useRef, useState } from 'react'
import { type SearchHit, searchTasksQueryOptions } from '@/entities/task'
import { cn, useDebouncedValue } from '@/shared/lib'
import { Icon, type IconName } from '@/shared/ui/icon'
import { HitRow } from './hit-row'

const NAV: { to: string; label: string; icon: IconName }[] = [
  { to: '/', label: 'Мои задачи', icon: 'my-tasks' },
  { to: '/companies', label: 'Компании', icon: 'building' },
  { to: '/reports', label: 'Отчёты', icon: 'report' },
  { to: '/focus', label: 'Фокус', icon: 'focus' },
]

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function SectionTitle({ children }: { children: string }) {
  return <p className="label px-3 pt-3 pb-1">{children}</p>
}

function CommandRow({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean
  icon: IconName
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded-control px-3 py-2 text-left text-sm transition-colors',
        active ? 'bg-bg-elevated text-text-primary' : 'text-text-secondary hover:bg-bg-elevated',
      )}
    >
      <Icon name={icon} size={16} className="shrink-0 text-text-tertiary" />
      <span className="truncate">{label}</span>
    </button>
  )
}

// Глобальная ⌘K-палитра: FTS по задачам/тредам + быстрая навигация. Монтируется в
// pages/__root рядом с AppShell (page→widgets). Открытие — проп + глобальный ⌘K.
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounced = useDebouncedValue(query, 200)
  const trimmed = debounced.trim()
  const search = useQuery(searchTasksQueryOptions(trimmed))

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        onOpenChange(!open)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onOpenChange])

  // autoFocus запрещён линтером → ref + эффект на открытие.
  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  if (!open) return null

  const close = () => {
    onOpenChange(false)
    setQuery('')
    setActive(0)
  }

  const q = query.trim()
  const hits = search.data ?? []
  const navMatches = NAV.filter((n) => q === '' || n.label.toLowerCase().includes(q.toLowerCase()))
  const searching = trimmed.length > 0 && search.isFetching
  const noHits = trimmed.length > 0 && !search.isFetching && hits.length === 0

  const go = (to: string) => {
    void navigate({ to })
    close()
  }
  // Навигация по kind хита (url ответа = источник истины). project своей страницы
  // не имеет — открываем в контексте компании.
  const goHit = (hit: SearchHit) => {
    if (hit.kind === 'company') {
      void navigate({ to: '/companies/$companyId', params: { companyId: hit.id } })
    } else if (hit.kind === 'board') {
      void navigate({ to: '/boards/$boardId', params: { boardId: hit.id } })
    } else if (hit.kind === 'project') {
      if (hit.companyId) {
        void navigate({ to: '/companies/$companyId', params: { companyId: hit.companyId } })
      } else {
        void navigate({ to: '/companies' })
      }
    } else {
      void navigate({ to: '/tasks/$taskId', params: { taskId: hit.id } })
    }
    close()
  }
  const goSearchPage = () => {
    void navigate({ to: '/search', search: { q } })
    close()
  }

  const showAction = q.length > 0
  // Плоский список активируемых по Enter действий, в порядке отрисовки.
  const activations = [
    ...(showAction ? [goSearchPage] : []),
    ...hits.map((h) => () => goHit(h)),
    ...navMatches.map((n) => () => go(n.to)),
  ]
  const total = activations.length
  const activeIdx = Math.min(active, Math.max(0, total - 1))
  const actionCount = showAction ? 1 : 0
  const hitStart = actionCount
  const navStart = actionCount + hits.length

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => Math.min(i + 1, total - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      activations[activeIdx]?.()
    }
  }

  return (
    // dvh вместо vh: на мобиле открытая клавиатура ужимает вьюпорт, и панель,
    // отмеренная от полной высоты экрана, уезжает под неё.
    <div className="fixed inset-0 z-50 flex justify-center px-4 pt-[8dvh] sm:pt-[12dvh]">
      <button
        type="button"
        aria-label="Закрыть поиск"
        onClick={close}
        className="absolute inset-0 bg-black/50"
      />
      <div className="relative z-10 h-fit w-full max-w-xl overflow-hidden rounded-card border border-border-strong bg-bg-surface shadow-lg">
        <div className="relative flex items-center gap-3 border-b border-border-default px-4">
          <Icon name="search" size={18} className="shrink-0 text-text-tertiary" />
          <input
            ref={inputRef}
            type="text"
            aria-label="Поиск задач и команд"
            value={query}
            placeholder="Поиск задач, тредов, команд…"
            onChange={(e) => {
              setQuery(e.target.value)
              setActive(0)
            }}
            onKeyDown={onKeyDown}
            className="w-full bg-transparent py-4 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none"
          />
          {searching && (
            <span
              aria-hidden
              className="absolute inset-x-0 bottom-0 h-0.5 animate-pulse rounded-pill bg-border-accent"
            />
          )}
        </div>

        <div className="max-h-[60dvh] overflow-y-auto overscroll-contain p-2">
          {showAction && (
            <section aria-label="Действия">
              <SectionTitle>Действия</SectionTitle>
              <CommandRow
                active={activeIdx === 0}
                icon="search"
                label={`Искать «${q}» на странице`}
                onClick={goSearchPage}
              />
            </section>
          )}

          {q.length > 0 && (
            <section aria-label="Задачи">
              <SectionTitle>Задачи</SectionTitle>
              {noHits ? (
                <p className="px-3 py-2 text-sm text-text-tertiary">
                  Ничего не нашлось. Проверьте запрос.
                </p>
              ) : (
                hits.map((h, i) => (
                  <HitRow
                    key={`${h.kind}:${h.id}`}
                    hit={h}
                    active={activeIdx === hitStart + i}
                    onSelect={close}
                  />
                ))
              )}
            </section>
          )}

          {navMatches.length > 0 && (
            <section aria-label="Перейти">
              <SectionTitle>Перейти</SectionTitle>
              {navMatches.map((n, i) => (
                <CommandRow
                  key={n.to}
                  active={activeIdx === navStart + i}
                  icon={n.icon}
                  label={n.label}
                  onClick={() => go(n.to)}
                />
              ))}
            </section>
          )}

          {q.length === 0 && (
            <p className="px-3 pt-2 pb-1 text-xs text-text-tertiary">
              Поиск по задачам и тредам. Точная фраза — в кавычках, «-» исключает слово.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
